/*
 * <nl-w2-marquee data-speed="40"> (sections/nl-w2-marquee.liquid)
 *   [data-marquee-row] > [data-marquee-track] > [data-marquee-group] + clone
 *   button[data-marquee-toggle][aria-pressed] (hidden until the band animates)
 *
 * The CSS animation does the work (transform only). This script only refines it:
 *   - constant speed: duration = width of one group / data-speed (px per second), updated on resize and font load
 *   - pauses while the band is off screen (data-offscreen) to save the compositor
 *   - pause toggle for WCAG 2.2.2: a constant "Pozastavit" label, aria-pressed=true while paused (data-paused)
 * Does nothing under reduced motion or with the theme motion setting off: the band is static then.
 */
(() => {
  'use strict';

  if (customElements.get('nl-w2-marquee')) return;

  const motionOff = () =>
    (window.NL && window.NL.motion && window.NL.motion.reduced) ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    document.documentElement.classList.contains('nl-motion-off');

  class W2Marquee extends HTMLElement {
    connectedCallback() {
      if (this._bound || motionOff()) return;
      this.rows = Array.from(this.querySelectorAll('[data-marquee-row]'));
      if (!this.rows.length) return;
      this._bound = true;
      this.speed = Math.max(8, parseFloat(this.dataset.speed) || 40);

      this.toggle = this.querySelector('[data-marquee-toggle]');
      if (this.toggle) {
        this.toggle.hidden = false;
        this.onToggle = () => this.setPaused(this.toggle.getAttribute('aria-pressed') !== 'true');
        this.toggle.addEventListener('click', this.onToggle);
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.queueMeasure());
        this.rows.forEach((row) => {
          const group = row.querySelector('[data-marquee-group]');
          if (group) this.resizeObserver.observe(group);
        });
      }
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.queueMeasure());
      this.measure();

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.toggleAttribute('data-offscreen', !entry.isIntersecting);
        });
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.observer) this.observer.disconnect();
      if (this.toggle && this.onToggle) this.toggle.removeEventListener('click', this.onToggle);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this._bound = false;
    }

    queueMeasure() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.measure();
      });
    }

    measure() {
      this.rows.forEach((row) => {
        const group = row.querySelector('[data-marquee-group]');
        if (!group) return;
        const width = group.getBoundingClientRect().width;
        if (!width) return;
        row.style.setProperty('--nl-w2-dur', `${(width / this.speed).toFixed(2)}s`);
      });
    }

    setPaused(paused) {
      this.toggleAttribute('data-paused', paused);
      if (!this.toggle) return;
      this.toggle.setAttribute('aria-pressed', String(paused));
    }
  }

  customElements.define('nl-w2-marquee', W2Marquee);
})();
